import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import PathwaySwitcher from "./PathwaySwitcher";

const LINKS = [
  { label: "Services", id: "services" },
  { label: "Approach", id: "approach" },
  { label: "Contact", id: "contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-5 lg:px-12">
        <button
          data-testid="logo-home"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className={`font-serif text-2xl font-semibold tracking-tight transition-colors ${
            scrolled ? "text-ink" : "text-white"
          }`}
        >
          Prospera
        </button>

        <div className="hidden items-center gap-10 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className={`group relative font-mono text-xs uppercase tracking-[0.18em] transition-colors ${
                scrolled ? "text-ink/70 hover:text-cobalt" : "text-white/70 hover:text-white"
              }`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-cobalt transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <PathwaySwitcher scrolled={scrolled} />
          <button
            data-testid="nav-cta"
            onClick={() => go("contact")}
            className={`group flex items-center gap-2 border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
              scrolled
                ? "border-cobalt bg-cobalt text-white hover:bg-cobalt-dark"
                : "border-white/40 text-white hover:bg-white hover:text-ink"
            }`}
          >
            Start a project
            <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`md:hidden ${scrolled ? "text-ink" : "text-white"}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden bg-white md:hidden"
          >
            <div className="flex flex-col gap-2 px-6 py-6">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="border-b border-black/5 py-4 text-left font-serif text-2xl text-ink"
                >
                  {l.label}
                </button>
              ))}
              <PathwaySwitcher variant="mobile" />
              <button
                onClick={() => go("contact")}
                className="mt-4 bg-cobalt py-4 font-mono text-xs uppercase tracking-[0.15em] text-white"
              >
                Start a project
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
